/**
 * Puts an entry of an order's history (see OrderEvent in the API) into words:
 * what happened, who did it, and, for edits and repricing, what changed.
 *
 * @typedef {import("../../../api/types").OrderEvent} OrderEvent
 */
import {formatDateTime, formatMoney} from "../../../shared/utils/format";

const TITLES = {
    created: {provider: "Ordem criada"},
    edited: {provider: "Ordem editada"},
    prices_refreshed: {provider: "Preços atualizados"},
    sent: {provider: "Marcada como enviada"},
    confirmed: {provider: "Marcada como confirmada", customer: "Confirmada pelo cliente"},
    completed: {provider: "Marcada como concluída"},
    paid: {provider: "Marcada como paga"},
    canceled: {provider: "Cancelada", customer: "Cancelada pelo cliente"},
    expired: {system: "Expirou sem resposta do cliente"},
};

/** Who did it: the provider's user, the customer, or the system. */
export const actorLabel = (event) => {
    if (event.actor === "customer") return "Cliente";
    if (event.actor === "system") return "Sistema";
    return event.user_name || "Prestador";
};

const sameJson = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Lines of an order, grouped by catalog entry (service or product): the first of
// each entry in the "before" is the one the "after" starts from, and so on.
const byEntry = (lines, idKey) => {
    const groups = new Map();
    for (const line of lines ?? []) {
        groups.set(line[idKey], [...(groups.get(line[idKey]) ?? []), line]);
    }
    return groups;
};

// `idKey` is the line's reference to the catalog entry, `noun` what to call the entry when it comes or goes.
const lineChanges = (before, after, money, idKey, noun) => {
    const lines = [];
    const was = byEntry(before, idKey);
    const now = byEntry(after, idKey);
    for (const id of new Set([...was.keys(), ...now.keys()])) {
        const olds = was.get(id) ?? [];
        const news = now.get(id) ?? [];
        olds.slice(0, news.length).forEach((old, index) => {
            const current = news[index];
            if (old.quantity !== current.quantity) {
                lines.push(`${current.name}: quantidade ${old.quantity} → ${current.quantity}`);
            }
            if (old.base_value !== current.base_value) {
                lines.push(`${current.name}: preço ${money(old.base_value)} → ${money(current.base_value)}`);
            }
        });
        for (const gone of olds.slice(news.length)) lines.push(`${noun} removido: ${gone.name}`);
        for (const added of news.slice(olds.length)) lines.push(`${noun} adicionado: ${added.name}`);
    }
    return lines;
};

/** What differs between two snapshots of an order, one short line each. */
export const changes = (before, after, currency) => {
    const money = (value) => formatMoney(value, currency);
    const lines = [];
    if (before.total_value !== after.total_value) {
        lines.push(`Total: ${money(before.total_value)} → ${money(after.total_value)}`);
    }
    if (before.client_id !== after.client_id) lines.push("Cliente alterado");
    if ((before.observations ?? "") !== (after.observations ?? "")) lines.push("Observações alteradas");
    if (
        !sameJson(
            [before.discount_type, before.discount_value, before.discount_description],
            [after.discount_type, after.discount_value, after.discount_description]
        )
    ) {
        lines.push("Desconto alterado");
    }
    lines.push(...lineChanges(before.items, after.items, money, "service_item_id", "Serviço"));
    // older entries have no products
    lines.push(...lineChanges(before.products, after.products, money, "product_id", "Produto"));
    if (!sameJson(before.devices, after.devices)) {
        const [was, now] = [before.devices?.length ?? 0, after.devices?.length ?? 0];
        lines.push(was === now ? "Equipamentos alterados" : `Equipamentos: ${was} → ${now}`);
    }
    return lines;
};

/**
 * @param {OrderEvent} event
 * @param {string} [currency]
 * @returns {{title: string, who: string, when: string, details: string[]}}
 */
export const describeEvent = (event, currency) => {
    const data = /** @type {any} */ (event.data) ?? {};
    const title = TITLES[event.kind]?.[event.actor] ?? TITLES[event.kind]?.provider ?? "Alteração na ordem";
    /** @type {string[]} */
    let details = [];

    if (event.kind === "created" && data.after) {
        details = [`Total: ${formatMoney(data.after.total_value, currency)}`];
    } else if ((event.kind === "edited" || event.kind === "prices_refreshed") && data.before && data.after) {
        details = changes(data.before, data.after, currency);
        if (!details.length) details = ["Nenhuma diferença nos dados."];
    } else if (event.kind === "expired" && data.expiration) {
        details = [`Validade: ${formatDateTime(data.expiration)}`];
    }
    return {title, who: actorLabel(event), when: formatDateTime(event.created_at), details};
};
